// Teksty i drobne obliczenia ekranu panelu rodzica (czysty TS, testowany bez DOM).
import type { AdminProfile } from './adminApi.ts';
import { plPlural } from './plural.ts';

/** „Ostatnio widziane” po polsku: teraz / N min temu / N godz. temu / wczoraj / data. */
export function formatSeen(iso: string | null, now: Date): string {
  if (!iso) return 'jeszcze nie używane';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return 'brak danych';
  const min = Math.floor((now.getTime() - t) / 60_000);
  if (min < 2) return 'przed chwilą';
  if (min < 60) return `${min} min temu`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} godz. temu`;
  const d = Math.floor(h / 24);
  if (d === 1) return 'wczoraj';
  if (d < 14) return `${d} dni temu`;
  return new Date(t).toLocaleDateString('pl-PL');
}

/** Jedna linijka o profilu dziecka dla rodzica: postęp i kiedy urządzenie ostatnio zapisało go na serwerze. */
export function profileLine(p: Pick<AdminProfile, 'progress' | 'syncedAt'>, now: Date): string {
  const pr = p.progress;
  if (!pr || (pr.lessonsDone === 0 && pr.words === 0 && pr.stars === 0)) return `jeszcze bez postępu${p.syncedAt ? ` · zapis: ${formatSeen(p.syncedAt, now)}` : ''}`;
  const last = pr.lastLessonDate ? ` · ostatnia lekcja ${pr.lastLessonDate}` : '';
  return `${pr.lessonsDone} ${plPlural(pr.lessonsDone, 'lekcja', 'lekcje', 'lekcji')} · ${pr.words} ${plPlural(pr.words, 'słówko', 'słówka', 'słówek')} · ⭐ ${pr.stars} · seria ${pr.streak}${last} · zapis: ${formatSeen(p.syncedAt, now)}`;
}

/** Rozmiar plików po polsku (przecinek dziesiętny): 512 B, 48 KB, 2,3 MB. */
export function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return '0 B';
  if (n < 1024) return `${Math.round(n)} B`;
  if (n < 1024 * 1024 - 512) return `${Math.round(n / 1024)} KB`; // tuż pod 1 MB zaokrąglenie dałoby „1024 KB”
  return `${(Math.round((n / (1024 * 1024)) * 10) / 10).toString().replace('.', ',')} MB`;
}

/** Linijka o nagraniach głosu profilu (SPEC §23.39): ile ich jest, ile zajmują i kiedy była ostatnia próba. */
export function recordingLine(p: Pick<AdminProfile, 'recordVoice' | 'recordings'>, now: Date): string {
  const r = p.recordings;
  if (r.count === 0) return p.recordVoice ? 'zapis włączony · jeszcze bez nagrań' : 'zapis wyłączony · brak nagrań';
  return `${r.count} ${plPlural(r.count, 'nagranie', 'nagrania', 'nagrań')} · ${formatBytes(r.bytes)} · ostatnie: ${formatSeen(r.last, now)}`;
}
